import type { PaginatedExtendedGroupByQuery } from '@/api/generated/model';
import { computeSpendPerTask } from '@/domains/pages/ai-spend/utils/map-ai-spend-aggregates';
import type { SegmentKind } from '@/domains/pages/shared/segment-kind';
import { translate } from '@/utils/translations';

export type SegmentSpendSummary = {
  name: string;
  displayName?: string;
  cost: number;
  tasks: number;
  spendPerTask: number | null;
};

export function formatSegmentSpendDisplayName(name: string, kind: SegmentKind): string {
  if (kind === 'tenure') return translate('tenure', name);
  return name;
}

export function applySegmentSpendDisplayNames(
  segments: SegmentSpendSummary[],
  kind: SegmentKind,
): SegmentSpendSummary[] {
  if (kind !== 'tenure') return segments;

  return segments.map((segment) => ({
    ...segment,
    displayName: formatSegmentSpendDisplayName(segment.name, kind),
  }));
}

export function getSegmentSpendDisplayName(name: string, segments: SegmentSpendSummary[]): string {
  const segment = segments.find((item) => item.name === name);
  return segment?.displayName ?? name;
}

/** Peer group for a segment cut. Blank group names are dropped, mirroring `mapSegmentSummaries`. */
export function mapSegmentSpendSummaries(data: PaginatedExtendedGroupByQuery): SegmentSpendSummary[] {
  return data.data
    .map((row) => {
      const name = row.group_name?.trim() ?? '';
      const cost = row.cost ?? 0;
      const tasks = row.n_segments ?? 0;

      return {
        name,
        cost,
        tasks,
        spendPerTask: computeSpendPerTask(row.cost ?? null, tasks),
      };
    })
    .filter((segment) => segment.name.length > 0)
    .sort((left, right) => right.cost - left.cost);
}

/** Rank by spend per task, descending — #1 is the most expensive per task. */
export function computeSegmentSpendPerTaskRank(segments: SegmentSpendSummary[], name: string): number {
  const rankable = segments.filter((segment) => segment.spendPerTask != null);
  const sorted = [...rankable].sort((left, right) => right.spendPerTask! - left.spendPerTask!);
  const index = sorted.findIndex((segment) => segment.name === name);
  return index >= 0 ? index + 1 : 0;
}

export type SpendPerTaskRange = {
  cheapest: { name: string; value: number } | null;
  mostExpensive: { name: string; value: number } | null;
  rankedCount: number;
};

export function computeSpendPerTaskRange(segments: SegmentSpendSummary[]): SpendPerTaskRange {
  const rankable = segments.filter((segment) => segment.spendPerTask != null);
  if (rankable.length === 0) {
    return { cheapest: null, mostExpensive: null, rankedCount: 0 };
  }

  let cheapest = rankable[0]!;
  let mostExpensive = rankable[0]!;

  for (const segment of rankable) {
    if (segment.spendPerTask! < cheapest.spendPerTask!) cheapest = segment;
    if (segment.spendPerTask! > mostExpensive.spendPerTask!) mostExpensive = segment;
  }

  return {
    cheapest: { name: cheapest.name, value: cheapest.spendPerTask! },
    mostExpensive: { name: mostExpensive.name, value: mostExpensive.spendPerTask! },
    rankedCount: rankable.length,
  };
}
